import React, { useState, useEffect } from "react";
import { baseUrl, fmtMoney } from '../shared.js';
import '../pages/App.css';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import InputLabel from '@mui/material/InputLabel';
import FormControl from '@mui/material/FormControl';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import Typography from '@mui/material/Typography';

// A single labelled money row for the totals summary.
function TotalRow({ label, value, strong }) {
  return (
    <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 1, borderBottom: '1px solid var(--border)' }}>
      <Typography sx={{ fontWeight: strong ? 700 : 500, color: strong ? 'var(--text)' : 'var(--muted)' }}>{label}</Typography>
      <Typography className="app-mono" sx={{ fontWeight: 700, color: strong ? 'var(--primary)' : 'var(--text)' }}>{value}</Typography>
    </Box>
  );
}

function PayoutFunds() {
  const [consultantUsersLoaded, setConsultantUsersLoaded] = useState(false);
  var [consultantUserList, setConsultantUserList] = useState([]);
  const [userSelected, setUserSelected] = useState(false);
  const [createPayoutOpen, setCreatePayoutOpen] = useState(false);
  const [totals, setTotals] = useState({});
  const [totalsLoaded, setTotalsLoaded] = useState(false);
  const [payoutData, setPayoutData] = useState();
  const [confirmPayoutBoxOpen, setConfirmPayoutBoxOpen] = useState(false);
  const [confirmMessagePopup, setConfirmMessagePopup] = useState(false);
  const [confirmMessage, setConfirmMessage] = useState("");

  useEffect(() => {
    if (!consultantUsersLoaded && localStorage.getItem('canPayout') === '1') {
      setConsultantUsersLoaded(true);
      const url = baseUrl + 'tasks/consultUsers';
      fetch(url, {
        method: 'GET',
        headers: {
          'username': localStorage.getItem('username'),
          'bToken': localStorage.getItem('bToken'),
        },
      })
        .then((res) => res.json())
        .then((json) => setConsultantUserList(json))
        .catch((err) => console.log(err.message));
    }
  });

  const getTotals = () => {
    if (userSelected) {
      const url = baseUrl + "pandt/totals";
      fetch(url, {
        method: 'POST',
        mode: 'cors',
        headers: {
          'Content-Type': 'application/json',
          'username': localStorage.getItem('username'),
          'bToken': localStorage.getItem('bToken'),
        },
        body: ("json", JSON.stringify(payoutData)),
      })
        .then((response) => response.json())
        .then((data) => {
          if (data.status === 401) {
            setTotalsLoaded(false);
          } else {
            setTotals(data.result);
            setTotalsLoaded(true);
          }
        })
        .catch((error) => console.log(error));
    }
  };

  const createPayout = () => {
    const url = baseUrl + "payout/create";
    fetch(url, {
      method: 'POST',
      mode: 'cors',
      headers: {
        'Content-Type': 'application/json',
        'username': localStorage.getItem('username'),
        'bToken': localStorage.getItem('bToken'),
      },
      body: ("json", JSON.stringify(payoutData)),
    })
      .then((response) => response.json())
      .then((data) => {
        if (data.status !== 401) {
          setConfirmMessage(data.result);
          openConfirmMessage();
          openCreatePayout();
          openConfirmPayoutBox();
          getTotals();
        }
      })
      .catch((error) => console.log(error));
  };

  const openConfirmMessage = () => setConfirmMessagePopup((v) => !v);
  const openConfirmPayoutBox = () => setConfirmPayoutBoxOpen((v) => !v);
  const openCreatePayout = () => setCreatePayoutOpen((v) => !v);

  const chooseAnother = () => {
    setUserSelected(false);
    setPayoutData();
    setTotalsLoaded(false);
  };

  useEffect(() => {
    { !totalsLoaded ? getTotals() : setTotalsLoaded(true); }
  });

  if (localStorage.getItem('canPayout') !== '1') {
    return (
      <Card sx={{ p: 3, maxWidth: 520, mx: 'auto', textAlign: 'center' }}>
        <Typography sx={{ color: 'var(--muted)' }}>You do not have permission to record payouts.</Typography>
      </Card>
    );
  }

  const approvedSum = totals[0]?.sum ?? 0;
  const payoutSum = totals[1]?.sum ?? 0;

  return (
    <Box>
      {/* Create Payout modal */}
      <Dialog open={createPayoutOpen} onClose={openCreatePayout} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>Create Payout</DialogTitle>
        <DialogContent>
          <TextField fullWidth margin="dense" label="Payout Comments" multiline rows={4} variant="outlined" type="text" placeholder="Enter payout information here" onChange={(e) => setPayoutData({ ...payoutData, payoutComment: e.target.value })} />
          <TextField fullWidth margin="dense" label="Payout Amount" variant="outlined" type="number" onChange={(e) => setPayoutData({ ...payoutData, payoutAmount: e.target.value })} />
        </DialogContent>
        <DialogActions>
          <Button color="inherit" onClick={openCreatePayout}>Cancel</Button>
          <Button variant="contained" onClick={openConfirmPayoutBox}>Create Payout</Button>
        </DialogActions>
      </Dialog>

      {/* Confirm */}
      <Dialog open={confirmPayoutBoxOpen} onClose={openConfirmPayoutBox} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>Confirm Payout</DialogTitle>
        <DialogActions>
          <Button color="inherit" onClick={openConfirmPayoutBox}>Cancel</Button>
          <Button variant="contained" onClick={createPayout}>Confirm</Button>
        </DialogActions>
      </Dialog>

      {/* Message */}
      <Dialog open={confirmMessagePopup} onClose={openConfirmMessage} maxWidth="xs" fullWidth>
        <DialogContent>
          <Typography>{confirmMessage}</Typography>
        </DialogContent>
        <DialogActions>
          <Button variant="contained" onClick={openConfirmMessage}>OK</Button>
        </DialogActions>
      </Dialog>

      <Card sx={{ p: { xs: 2.5, md: 3.5 }, maxWidth: 560, mx: 'auto' }}>
        <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>Payout Money for Consultants</Typography>

        {userSelected ? (
          <Button variant="outlined" color="inherit" sx={{ color: 'var(--muted)', borderColor: 'var(--border-strong)', mb: 2 }} onClick={chooseAnother}>
            Clear Selected User
          </Button>
        ) : (
          <FormControl fullWidth margin="dense">
            <InputLabel id="consultUserLabel">Consultant User</InputLabel>
            <Select
              labelId="consultUserLabel"
              label="Consultant User"
              value={payoutData?.userId ?? ''}
              onChange={(e) => {
                setUserSelected(true);
                setPayoutData({ ...payoutData, userId: e.target.value });
                getTotals();
              }}
            >
              <MenuItem value="" disabled>Select Consultant User</MenuItem>
              {consultantUserList.map((u) => (
                <MenuItem value={u.id} key={u.id}>{u.username}</MenuItem>
              ))}
            </Select>
          </FormControl>
        )}

        {userSelected ? (
          <Box sx={{ mt: 1 }}>
            <TotalRow label="Approved Task Totals" value={fmtMoney(approvedSum)} />
            <TotalRow label="Payout Totals" value={fmtMoney(payoutSum)} />
            <TotalRow label="Balance" value={fmtMoney(approvedSum - payoutSum)} strong />
            <Button
              variant="contained"
              fullWidth
              sx={{ mt: 2.5 }}
              onClick={() => { setPayoutData({ ...payoutData, id: null, payoutComment: '', payoutAmount: '' }); openCreatePayout(); }}
            >
              Create Payout
            </Button>
          </Box>
        ) : null}
      </Card>
    </Box>
  );
}

export default PayoutFunds;
